import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching ALL modules
export const fetchAllModules = createAsyncThunk(
  "module/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.MODULE.SELECT_ALL);

      return response.data;
    } catch (error) {
      console.error(
        "❌ Modules API Error:",
        error.response?.status,
        error.response?.data,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch modules",
      );
    }
  },
);

// Thunk for fetching module by ID
export const fetchModuleById = createAsyncThunk(
  "module/fetchById",
  async (moduleId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.MODULE.SELECT}/${moduleId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Module not found",
      );
    }
  },
);

// Thunk for adding a new module
export const addModule = createAsyncThunk(
  "module/add",
  async (moduleData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.MODULE.INSERT,
        moduleData,
      );
      dispatch(fetchAllModules());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add module",
      );
    }
  },
);

// Thunk for updating an existing module
export const updateModule = createAsyncThunk(
  "module/update",
  async (moduleData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.MODULE.UPDATE,
        moduleData,
      );
      dispatch(fetchAllModules()); // Refresh list after update
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update module",
      );
    }
  },
);

// Thunk for deleting a module
export const deleteModule = createAsyncThunk(
  "module/delete",
  async (moduleId, { rejectWithValue, dispatch }) => {
    try {
      await axiosInstance.delete(
        `${API_ENDPOINTS.MODULE.DELETE}?id=${moduleId}`,
      );
      dispatch(fetchAllModules());
      return moduleId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete module",
      );
    }
  },
);

const moduleSlice = createSlice({
  name: "module",
  initialState: {
    modulesList: [],
    pagination: null,
    selectedModule: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearSelectedModule: (state) => {
      state.selectedModule = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Modules - FIXED
      .addCase(fetchAllModules.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllModules.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.modulesList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllModules.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch modules";
        console.error("❌ Modules API Failed:", action.payload);
      })
      // Fetch By ID
      .addCase(fetchModuleById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchModuleById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedModule = action.payload.data;
      })
      .addCase(fetchModuleById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Module
      .addCase(addModule.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addModule.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addModule.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Module
      .addCase(updateModule.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateModule.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateModule.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Module
      .addCase(deleteModule.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteModule.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteModule.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedModule } = moduleSlice.actions;
export default moduleSlice.reducer;
